import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
  Grid,
  useTheme,
  IconButton,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import { RecordModel } from "../models/RecordModel";
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useLazyGetStationReportListQuery } from "../features/station/StationSlice";
import { t } from 'i18next';
import { logOut } from '../features/auth/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import RecordTableV2, { TableRow, TableHeader } from "./RecordTableV2";

interface DownloadReportDialogProps {
  open: boolean;
  onClose: () => void;
}

const DownloadMonthlyReportDialog = ({ open, onClose }: DownloadReportDialogProps) => {

  const [getStationReportList] = useLazyGetStationReportListQuery();
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [data, setData] = useState<RecordModel<any>>();
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState<number>(5);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const columns: TableHeader[] = [
    { field: 'month', headerName: t("Month"), width: 200, isSortable: false },
    {
      field: 'downloadUrl', headerName: t("Action"), width: 600, isSortable: false,
      renderCell: (params: any) => (
        <Box>
          <Button sx={{marginRight:2}} variant="contained" onClick={() => handleDetailReportClick(params.row)}>{t('Detail Report')}</Button>
          <Button sx={{marginRight:2}} variant="contained" onClick={() => handleSummaryReportClick(params.row)}>{t('Summary Report')}</Button>
          <Button sx={{marginRight:2}} variant="contained" onClick={() => handleStatisticsReportClick(params.row)}>{t('Statistics Report')}</Button>
        </Box>
      )
    },
  ];

  const fetchDataForPage = async (pageNumber: any) => {
    // Fetch data for the specified page from the backend
    const data = await getStationReportList({
      pageSize: pageSize,
      sortBy: 'uploadDate',
      sortDirection: 'asc',
      page: pageNumber,
    });

    if (data.status === 'fulfilled') {

      return data.data.records.map((item: any) => ({
        id: item?.month,
        month: item?.month,
        detailDownloadUrl: item.detailDownloadUrl,
        summaryDownloadUrl: item?.summaryDownloadUrl,
        photoSummaryDownloadUrl: item?.photoSummaryDownloadUrl,
      }))
    }

    if (data.status === 'rejected') {
      alert(t('An error occurred. Please login again.'));
      dispatch(logOut());
      navigate('/');
    }

  };

  useEffect(() => {
    if (open) {
      try {
        setIsLoading(true);
        getStationReportList({
          pageSize: pageSize,
          sortBy: 'uploadDate',
          sortDirection: 'asc'
        }).then(
          (res) => {
            if (res.status === 'fulfilled') {

              const tableRows = res?.data.records.map((item: any) => ({
                id: item?.month,
                month: item?.month,
                detailDownloadUrl: item?.detailDownloadUrl,
                summaryDownloadUrl: item?.summaryDownloadUrl,
                photoSummaryDownloadUrl: item?.photoSummaryDownloadUrl,
              }))

              setTableRows(tableRows);
              setData(res.data);
              setIsLoading(false);
            }

            if (res.status === 'rejected') {
              alert(t('An error occurred. Please login again.'));
              dispatch(logOut());
              navigate('/');
            }

          }
        )
      } catch (error) {

      }
    }

  }, [open]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleDetailReportClick = async (rowData: any) => {
    try {

      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${rowData.detailDownloadUrl}?token=${token}`, {
        method: 'GET',
        headers: {
          // Authorization: `Bearer ${token}`,
          'Content-Type': 'application/pdf', // Adjust based on your API
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch the report.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const month = `${rowData.month.split('-')[1]}${rowData.month.split('-')[0]}`
      const filename = `Coastal_Detail_Rpt_${month}.pdf`;

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // onClose();
    } catch (err) {
      //setError('Failed to download the report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  const handleSummaryReportClick = async (rowData: any) => {
    try {

      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${rowData.summaryDownloadUrl}?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf', // Adjust based on your API
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch the report.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const month = `${rowData.month.split('-')[1]}${rowData.month.split('-')[0]}`
      const filename = `Coastal_Summary_Rpt_${month}.pdf`;

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // onClose();
    } catch (err) {
      //setError('Failed to download the report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  const handleStatisticsReportClick = async (rowData: any) => {
    try {

      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${rowData.photoSummaryDownloadUrl}?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf', // Adjust based on your API
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch the report.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const month = `${rowData.month.split('-')[1]}${rowData.month.split('-')[0]}`
      const filename = `Coastal_Statistics_Rpt_${month}.pdf`;

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // onClose();
    } catch (err) {
      //setError('Failed to download the report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t('Download Report')}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: 'auto' }}>
        {isLoading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 300
          }}>
            <CircularProgress size={60} />
          </Box>
        ) :
          <RecordTableV2
            columns={columns}
            rows={tableRows}
            checkbox={false}
            pages={data?.totalPages}
            totalCount={data?.totalCount}
            updateTableRows={setTableRows}
            fetchDataForPage={fetchDataForPage}
          ></RecordTableV2>
        }
      </DialogContent>
    </Dialog>
  );
};

export default DownloadMonthlyReportDialog;